import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";

interface TenantSettings {
  integrations: {
    google_places_api_key_configured?: boolean;
    google_places_api_key_masked?: string | null;
  };
}

interface GooglePlacesIntegrationPanelProps {
  canWrite: boolean;
}

export function GooglePlacesIntegrationPanel({ canWrite }: GooglePlacesIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [googlePlacesApiKey, setGooglePlacesApiKey] = useState("");
  const [googlePlacesConfigured, setGooglePlacesConfigured] = useState(false);
  const [googlePlacesMasked, setGooglePlacesMasked] = useState<string | null>(null);
  const [clearGooglePlacesApiKey, setClearGooglePlacesApiKey] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        const integ = settings.integrations;
        setGooglePlacesConfigured(integ?.google_places_api_key_configured ?? false);
        setGooglePlacesMasked(integ?.google_places_api_key_masked ?? null);
        setGooglePlacesApiKey("");
        setClearGooglePlacesApiKey(false);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch("/api/v1/settings/integrations", {
        ...(googlePlacesApiKey.trim()
          ? { google_places_api_key: googlePlacesApiKey.trim() }
          : {}),
        clear_google_places_api_key: clearGooglePlacesApiKey,
      });
      setSuccess(true);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <form onSubmit={(e) => void onSave(e)} className="space-y-5">
      <FormHint>
        Ohne API-Key geben Gäste die Adresse manuell ein. Beschränken Sie den Key in der Google
        Cloud Console auf Ihre Buchungs-Domain, um Missbrauch zu vermeiden.
      </FormHint>

      {googlePlacesConfigured ? (
        <div className="rounded-xl border border-status-green/30 bg-status-green/5 px-4 py-3">
          <p className="text-sm text-label">
            API-Key hinterlegt:{" "}
            <code className="text-xs text-label-secondary">{googlePlacesMasked ?? "••••"}</code>
          </p>
        </div>
      ) : (
        <p className="text-sm text-label-secondary">Noch kein API-Key hinterlegt.</p>
      )}

      {canWrite ? (
        <fieldset className="space-y-4" disabled={!canWrite}>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-label">
              {googlePlacesConfigured ? "Neuen API-Key setzen" : "Google Places API-Key"}
            </span>
            <input
              type="password"
              className="eh-input max-w-md font-mono text-xs"
              value={googlePlacesApiKey}
              onChange={(e) => {
                setGooglePlacesApiKey(e.target.value);
                if (e.target.value.trim()) setClearGooglePlacesApiKey(false);
              }}
              placeholder="AIza…"
              autoComplete="off"
            />
          </label>
          {googlePlacesConfigured ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={clearGooglePlacesApiKey}
                onChange={(e) => {
                  setClearGooglePlacesApiKey(e.target.checked);
                  if (e.target.checked) setGooglePlacesApiKey("");
                }}
              />
              Gespeicherten API-Key entfernen
            </label>
          ) : null}
        </fieldset>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">Einstellungen gespeichert.</p> : null}
      {canWrite ? (
        <div className="flex justify-end border-t border-separator pt-4">
          <Button type="submit" loading={saving}>
            Speichern
          </Button>
        </div>
      ) : null}
    </form>
  );
}
